/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-09-10
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React from 'react';
import { useReveal } from '../hooks/useReveal';
import {
  SHELL,
  EYEBROW_DARK,
  EYEBROW_LIGHT,
  H1,
  H2,
  LEAD,
  LEAD_DARK,
  RULE,
} from '../theme';

/**
 * Blocs de tete partages par les sept pages.
 *
 * Chaque page redefinissait son propre hero et ses propres titres de section :
 * memes intentions, valeurs differentes. Les deux composants ci-dessous fixent
 * la forme une fois pour toutes.
 */

/**
 * Hero des pages interieures.
 *
 * L'en-tete est en `fixed` et transparent en haut de page : le hero doit donc
 * reserver la hauteur de la barre (80 px) sous laquelle il passe, sinon le
 * titre se glisse dessous.
 */
export const PageHero = ({ eyebrow, title, lead, image, children }) => (
  <section
    data-testid="tchad-page-hero"
    className="relative overflow-hidden bg-slate-950 pb-20 pt-40 text-white lg:pb-28 lg:pt-48"
  >
    {image ? (
      <>
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/50" />
      </>
    ) : null}
    <div className="absolute inset-0 bg-tchad-weave" />
    <div className={`relative ${SHELL}`}>
      <div className="max-w-3xl">
        {eyebrow ? <span className={EYEBROW_DARK}>{eyebrow}</span> : null}
        <div className={`${RULE} mt-3`} />
        <h1 className={`${H1} mt-6`}>{title}</h1>
        {lead ? <p className={`mt-6 max-w-2xl ${LEAD_DARK}`}>{lead}</p> : null}
        {children ? <div className="mt-10">{children}</div> : null}
      </div>
    </div>
  </section>
);

/** Surtitre, filet or, titre, chapo — avec apparition au defilement. */
export const SectionHead = ({ eyebrow, title, lead, dark = false, align = 'start' }) => {
  const { ref, className } = useReveal();
  return (
    <div
      ref={ref}
      className={`${className} ${
        align === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'
      }`}
    >
      {eyebrow ? (
        <span className={dark ? EYEBROW_DARK : EYEBROW_LIGHT}>{eyebrow}</span>
      ) : null}
      <div className={`${RULE} mt-3 ${align === 'center' ? 'mx-auto' : ''}`} />
      <h2 className={`${H2} mt-5 ${dark ? 'text-white' : 'text-slate-950'}`}>{title}</h2>
      {lead ? <p className={`mt-5 ${dark ? LEAD_DARK : LEAD}`}>{lead}</p> : null}
    </div>
  );
};
